import '@babel/polyfill';
import { LicenseManager } from '@ag-grid-enterprise/core'; // eslint-disable-line
import React, { Component } from 'react';
import { createRoot } from 'react-dom/client';
import { createInstance, OptimizelyProvider } from '@optimizely/react-sdk'; // eslint-disable-line
import { BrowserRouter as Router } from 'react-router-dom';
import * as Sentry from '@sentry/react';

// imports modules from common package
import { Authentication, Layout, Modal, Utils, UI, Chatbot, FeatureFlags, Toast } from 'common';
import ContactsCommunication from './components/utils/ContactsCommunication';
import MainView from './components/MainView.component';

Sentry.init({
	dsn: 'https://2bf0cdb2c7121a2c231715a123b5bbb1@o106853.ingest.us.sentry.io/4509831239499776',
	// Setting this option to true will send default PII data to Sentry.
	// For example, automatic IP address collection on events
	sendDefaultPii: true,
	environment: process.env.NODE_ENV === 'production' ? 'contacts' : 'contacts-dev',
});

const { Viewport } = Layout;
const { ModalContainer } = Modal;
const { Analytics, Auth0Util, Session, StyleLoader, Navigation, BillingPlans, Util } = Utils;
const { Spinner } = UI;
const { SignIn } = Authentication;
const { ChatbotInitializer } = Chatbot;
const { InitializeLaunchDarkly } = FeatureFlags;
const { ToastProvider } = Toast;

LicenseManager.setLicenseKey(__AG_GRID_LICENSE__);

export default class App extends Component {
	constructor(props) {
		super(props);

		const page = 'Contacts';

		// initialize Session with sessionTimeout handler
		Session.init(
			{
				Page: page,
			},
			() => {
				ChatbotInitializer.logout();

				if (Auth0Util.isEligibleForAuth0()) {
					Session.signOut(() => {
						Auth0Util.signOut();
					});
				} else {
					this.setState({
						showSignIn: true,
					});
				}
			}
		);

		// initialize ContactsCommunication
		ContactsCommunication.init({
			resetTimeout: Session.resetTimeout,
			triggerTimeout: Session.triggerTimeout,
		});

		this.state = {
			data: {
				InternalAccountInfo: true,
			},
			page,
			showMask: false,
			showSignIn: false,
			stylesLoaded: false,
			auth0Error: false,
		};

		this.verifyAuthAndLoadSession();
	}

	sessionLoadCallback = async () => {
		const { page } = this.state;
		const sessionData = Session.getData();
		const { brand, isSignedIn, settings } = sessionData;
		const optimizelyClient = createInstance({ sdkKey: settings.OptimizelyKey });
		const isAccountVerificationRequired = Session.isAccountVerificationRequired();

		// update document title
		document.title = `${brand.brandName} | ${page}`;

		// load the theme bundle
		StyleLoader.loadTheme(
			brand.brandName,
			this.setState({ showMask: true, stylesLoaded: true })
		);

		const completeAuth = Util.isInRoute('completeAuth') === true;

		if (!isSignedIn && completeAuth) {
			const completeAuthResponse = await Auth0Util.completeAuth();

			if (completeAuthResponse?.isSuccessful === true) {
				Navigation.redirect(completeAuthResponse.url);
				return;
			} else {
				this.setState({
					auth0Error: true,
				});
			}
		}

		if (isAccountVerificationRequired) {
			await Navigation.redirectToAccountVerify();
		}

		// initialize analytics
		Analytics.set({
			...sessionData,
			role: Session.getUserRole(),
		});

		if (isSignedIn) {
			await BillingPlans.init();
		}

		const LDProvider = await InitializeLaunchDarkly();
		ChatbotInitializer.start();

		this.setState({
			showMask: false,
			showSignIn: isSignedIn !== true,
			optimizelyClient,
			analyticsMetadata: Analytics.metadata,
			LDProvider,
		});
	};

	signInCallback = ({ status }) => {
		if (status) {
			this.setState(
				{
					showMask: true,
					showSignIn: false,
				},
				this.verifyAuthAndLoadSession
			);
			ChatbotInitializer.resetChatMessengerUserPrefs();
		}
	};

	verifyAuthAndLoadSession = () => {
		const { data } = this.state;
		Session.verifyAuthAndLoad(data, this.sessionLoadCallback);
	};

	render() {
		const {
			showMask,
			showSignIn,
			optimizelyClient,
			analyticsMetadata,
			LDProvider,
			stylesLoaded,
			auth0Error,
		} = this.state;

		if (!stylesLoaded) {
			return null;
		} else if (showMask) {
			return <Spinner msg="Loading Contacts" />;
		} else if (showSignIn) {
			if (auth0Error === false && Auth0Util.isEligibleForAuth0()) {
				Auth0Util.launchSignInPage();
				return null;
			}

			return (
				<Viewport shouldScroll={showSignIn}>
					<ModalContainer>
						<ToastProvider>
							<Router basename={__BASENAME__}>
								<SignIn
									signInCallback={this.signInCallback}
									isAuth0Error={auth0Error}
								/>
							</Router>
						</ToastProvider>
					</ModalContainer>
				</Viewport>
			);
		}

		return (
			<ToastProvider>
				<OptimizelyProvider
					optimizely={optimizelyClient}
					user={{
						id: analyticsMetadata.visitor.id.toString(),
						attributes: analyticsMetadata.visitor,
					}}
				>
					{LDProvider ? (
						<LDProvider>
							<MainView />
						</LDProvider>
					) : (
						<MainView />
					)}
				</OptimizelyProvider>
			</ToastProvider>
		);
	}
}

const root = createRoot(document.getElementById('root'));
root.render(<App />);
